import { Resources } from 'constants/resources';

import { useGoBack } from 'hooks/useGoBack';
import { useStagedDelete } from 'hooks/useStagedDelete';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { useNotify } from 'react-admin';
import { CancelButton } from 'shared/mui/Buttons';
import { CardHeader } from 'shared/mui/Card';
import { Dialog } from 'shared/mui/Dialogs';
import { DeleteStageDialogDenyReason } from 'shared/mui/Dialogs/DeleteStageDialog/DeleteStageDialogDenyReason';
import { DeleteStageDialogForm } from 'shared/mui/Dialogs/DeleteStageDialog/DeleteStageDialogForm';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Typography } from 'shared/mui/Typography';
import { TypographyBold } from 'shared/mui/Typography/TypographyBold';
import { Company } from 'types/embedded/company';

import { StyledBox } from './CompaniesDeleteDialog.styled';

type CompaniesDeleteDialogProps = {
  record?: Company;
  open: boolean;
  onClose: () => void;
};

export const CompaniesDeleteDialog: React.FC<CompaniesDeleteDialogProps> = ({
  record,
  open,
  onClose,
}) => {
  const translate = useTypedTranslate();
  const { goBack } = useGoBack();
  const notify = useNotify();
  const { deleteOneRecord, denyDeleteReason, stage } = useStagedDelete({
    record,
    onSuccess: async () => {
      onClose();
      goBack();
      notify(
        <>
          Компания <TypographyBold text={record?.name || ''} /> удалена
        </>,
        { type: 'success' },
      );
      await queryClient.refetchQueries({ queryKey: [Resources.Companies] });
    },
  });

  return (
    <Dialog onClose={onClose} open={open} width="640px">
      <StyledBox>
        <CardHeader
          appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
          title={translate('app.companies.deleteTitle')}
          titleSize="smallBold"
        />
        <DeleteStageDialogForm
          deleteOneRecord={deleteOneRecord}
          details={
            <Typography>
              {translate('app.companies.deleteDetails1')}{' '}
              <TypographyBold fontSize={16} text={record?.name || ''} />{' '}
              {translate('app.companies.deleteDetails2')}
            </Typography>
          }
          onClose={onClose}
          open={true}
          stage={stage}
        >
          <DeleteStageDialogDenyReason reason={denyDeleteReason}>
            <CancelButton
              label={translate('app.companies.returnToList')}
              linkType="exit"
              sx={{ marginLeft: 'auto' }}
              variant="outlined"
            />
          </DeleteStageDialogDenyReason>
        </DeleteStageDialogForm>
      </StyledBox>
    </Dialog>
  );
};
